import { LoadingState } from "../../../domain/data-layer/loading-state";
import React, { useEffect, useMemo } from "react";
import { useEqualState } from "../../../hooks";
import { DealerWithLinkT } from "@swo/storefront";
import { DealerWithCoordinatesT } from "../queries/get-dealers-by-query";

const icon = "/assets/dealer-locator/map-pin.png";
const iconCenter = "/assets/dealer-locator/map-pin-center.png";
const iconMyLocation = "/assets/dealer-locator/my-location-pin.svg";

type ExtendedDealerInfoSummary = DealerWithCoordinatesT & { isSelected: boolean };

export function useMapMarkers(
  data: LoadingState<{ dealers: Array<DealerWithLinkT> }>,
  selectedDealerId: string | null,
  mapInstanceHandle: google.maps.Map | null,
  onDealerMarkerClicked: (dealerId: string) => void,
) {
  const [state, setState] = useEqualState<{
    dealers: Array<ExtendedDealerInfoSummary>;
  } | null>(null);

  useEffect(() => {
    if (data.__tag === "loaded") {
      setState({
        dealers: data.data.dealers
          .map(d => ({ ...d, isSelected: d.id === selectedDealerId }))
          .filter<ExtendedDealerInfoSummary>((d): d is ExtendedDealerInfoSummary => !!d.address?.coordinates),
      });
    }
  }, [data, selectedDealerId]);

  return useMemo(() => {
    if (state && mapInstanceHandle) {
      const selectedMarker = searchCenterMarkerImage(mapInstanceHandle);
      const unselectedMarker = markerImage(mapInstanceHandle);

      return state.dealers.map(dealer => {
        return (
          <MarkerFakeComponent
            key={dealer.id}
            mapInstanceHandle={mapInstanceHandle}
            dealer={{
              name: dealer.name,
              data: dealer.id,
              coordinates: dealer.address.coordinates,
              isSelected: dealer.isSelected,
            }}
            onDealerMarkerClicked={onDealerMarkerClicked}
            icon={dealer.isSelected ? selectedMarker : unselectedMarker}
          />
        );
      });
    } else {
      return null;
    }
  }, [state, mapInstanceHandle, onDealerMarkerClicked]);
}

type MarkerFakeComponentProps<T> = {
  dealer: { name: string; coordinates: { latitude: number; longitude: number }; isSelected: boolean; data: T };
  mapInstanceHandle: google.maps.Map;
  onDealerMarkerClicked?: (data: T) => void;
  icon: google.maps.Icon;
};

export function MarkerFakeComponent<T>(props: MarkerFakeComponentProps<T>) {
  useEffect(() => {
    const m = getMarker(
      props.mapInstanceHandle,
      { coordinates: props.dealer.coordinates, name: props.dealer.name, isSelected: props.dealer.isSelected },
      props.icon,
    );

    m.setMap(props.mapInstanceHandle);

    const maybeOnClick = props.onDealerMarkerClicked;

    const listenerUnsubscribe = maybeOnClick
      ? m.addListener("click", function () {
          maybeOnClick(props.dealer.data);
        })
      : maybeOnClick;

    return () => {
      listenerUnsubscribe && listenerUnsubscribe.remove();
      m.setMap(null);
    };
  }, [props.dealer.data, props.dealer.isSelected, props.onDealerMarkerClicked]);

  return null;
}

function getMarker(
  _mapInstanceEvidence: google.maps.Map,
  dealer: { name: string; coordinates: { latitude: number; longitude: number }; isSelected: boolean },
  icon: google.maps.Icon,
) {
  return generateMarkerForDealer(
    _mapInstanceEvidence,
    { name: dealer.name, coordinates: dealer.coordinates },
    dealer.isSelected,
    icon,
  );
}

export const markerImage: (_mapInstanceEvidence: google.maps.Map) => google.maps.Icon = _evidence => {
  const size = { width: 75 / 3, height: 96 / 3 };

  return {
    url: icon,
    size: new google.maps.Size(size.width, size.height),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(size.width / 2, size.height - 5 / 3),
  };
};

export const searchCenterMarkerImage: (_mapInstanceEvidence: google.maps.Map) => google.maps.Icon = _evidence => {
  const size = { width: 75 / 2, height: 96 / 2 };

  return {
    url: iconCenter,
    size: new google.maps.Size(size.width, size.height),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(size.width / 2, size.height - 5 / 2),
  };
};

export const myLocationMarkerImage: (_mapInstanceEvidence: google.maps.Map) => google.maps.Icon = _evidence => {
  const size = { width: 36, height: 36 };

  return {
    url: iconMyLocation,
    size: new google.maps.Size(size.width, size.height),
    origin: new google.maps.Point(0, 0),
    anchor: new google.maps.Point(size.width / 2, size.height / 2),
  };
};

// window.google is loaded asynchronously.
// This functions require instance of google.map.Map just to serve a purpose of
// having google already initialized
function generateMarkerForDealer(
  _mapInstanceEvidence: google.maps.Map,
  dealer: { name: string; coordinates: DealerWithCoordinatesT["address"]["coordinates"] },
  isSelected: boolean,
  icon: google.maps.Icon,
) {
  const position = new google.maps.LatLng(dealer.coordinates.latitude, dealer.coordinates.longitude);

  if (isSelected) {
    return new google.maps.Marker({
      position: position,
      title: dealer.name,
      icon: icon,
      clickable: true,
    });
  } else {
    return new google.maps.Marker({
      position: position,
      title: dealer.name,
      icon: icon,
      clickable: true,
    });
  }
}
